import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/authContext';
import { db } from '../firebase';
import { collection, setDoc, serverTimestamp, doc, getDoc, getDocs, deleteDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';

const ShareStory = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    text: '',
    metrics: {
      spiritualDiscussions: 0,
      heardGospel: 0,
      experiencedLove: 0,
      believersEncouraged: 0,
      newFollowers: 0
    },
    sharing: {
      organizations: []
    }
  });

  const [userOrganizations, setUserOrganizations] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch user's organizations
  useEffect(() => {
    const fetchUserOrgs = async () => {
      if (!currentUser?.uid) return;
      try {
        const userOrgsRef = collection(db, 'users', currentUser.uid, 'organizations');
        const userOrgsSnap = await getDocs(userOrgsRef);
        
        const activeOrgs = await Promise.all(
          userOrgsSnap.docs
            .filter(doc => doc.data().status === 'active')
            .map(async (membershipDoc) => {
              const membershipData = membershipDoc.data();
              const orgDoc = await getDoc(doc(db, 'organizations', membershipDoc.id));
              
              if (!orgDoc.exists()) return null;

              const orgData = orgDoc.data();
              return {
                id: membershipDoc.id,
                name: orgData.name,
                displayName: orgData.displayName || orgData.name,
                role: membershipData.role,
                status: membershipData.status
              };
            })
        );

        const validOrgs = activeOrgs.filter(org => org !== null);
        setUserOrganizations(validOrgs);
      } catch (error) {
        console.error('Error fetching organizations:', error);
      }
    };

    fetchUserOrgs();
  }, [currentUser]);

  const handleMetricChange = (metric, value) => {
    const newValue = Math.max(0, parseInt(value) || 0);
    setFormData(prev => ({
      ...prev,
      metrics: {
        ...prev.metrics,
        [metric]: newValue
      }
    }));
  };

  const handleOrgToggle = (orgId) => {
    setFormData(prev => ({
      ...prev,
      sharing: {
        ...prev.sharing,
        organizations: prev.sharing.organizations.includes(orgId)
          ? prev.sharing.organizations.filter(id => id !== orgId)
          : [...prev.sharing.organizations, orgId]
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Generate story ID using Firebase auto-ID for consistency
      const storyId = doc(collection(db, 'stories')).id;
      
      // Build complete story data
      const storyData = {
        id: storyId,
        text: formData.text,
        authorId: currentUser.uid,
        authorName: currentUser.displayName,
        authorEmail: currentUser.email,
        authorPhotoURL: currentUser.photoURL,
        createdAt: serverTimestamp(),
        metrics: formData.metrics,
        sharing: {
          private: false,
          organizations: formData.sharing.organizations
        },
        status: 'active'
      };

      // Save to user's stories
      await setDoc(doc(db, 'users', currentUser.uid, 'stories', storyId), storyData);

      // If not private, add to stories-feed
      if (!storyData.sharing.private) {
        await setDoc(doc(db, 'stories-feed', storyId), storyData);
      }

      // Share with selected organizations - save FULL story data (not just metadata)
      if (formData.sharing.organizations.length > 0) {
        await Promise.all(
          formData.sharing.organizations.map(orgId =>
            setDoc(doc(db, 'organizations', orgId, 'stories', storyId), storyData)
          )
        );
      }

      navigate('/');
    } catch (error) {
      console.error('Error sharing story:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white sm:bg-gray-50 sm:py-6">
      <div className="max-w-2xl mx-auto bg-white sm:rounded-2xl sm:shadow-sm">
        {/* Header - Smaller, tighter spacing */}
        <div className="text-center p-4 sm:p-6">
          <h1 className="text-xl font-bold text-gray-900">Share that Story</h1>
          <p className="text-sm text-gray-500 mt-1">How is God working through you to reach others?</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-4">
          {/* Story Input - Compact but still usable */}
          <div className="bg-gray-50 rounded-xl p-3">
            <textarea
              rows="5"
              className="block w-full bg-transparent border-0 focus:ring-0 text-sm resize-none placeholder:text-gray-400"
              placeholder="Type your story here..."
              value={formData.text}
              onChange={(e) => setFormData(prev => ({ ...prev, text: e.target.value }))}
            />
          </div>

          {/* Impact Metrics - Compact grid */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Impact Metrics</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                { id: 'experiencedLove', label: "God's Love" },
                { id: 'spiritualDiscussions', label: 'Discussions' },
                { id: 'heardGospel', label: 'Gospel' },
                { id: 'believersEncouraged', label: 'Encouraged' },
                { id: 'newFollowers', label: 'New Followers' }
              ].map((metric) => (
                <div 
                  key={metric.id} 
                  className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2"
                >
                  <span className="text-sm text-gray-600">{metric.label}</span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleMetricChange(metric.id, formData.metrics[metric.id] - 1)}
                      className="w-6 h-6 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-400 hover:bg-gray-50"
                    >
                      -
                    </button>
                    <span className="w-4 text-center text-sm font-medium">
                      {formData.metrics[metric.id]}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleMetricChange(metric.id, formData.metrics[metric.id] + 1)}
                      className="w-6 h-6 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-400 hover:bg-gray-50"
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sharing Settings - Compact list */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Share with Organizations</h3>
            <div className="space-y-1">
              {userOrganizations.map(org => (
                <div
                  key={org.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <label className="flex-1 font-medium text-gray-700">
                    {org.displayName}
                  </label>
                  <div className="relative inline-block w-11 h-5">
                    <input
                      type="checkbox"
                      id={`org-switch-${org.id}`}
                      checked={formData.sharing.organizations.includes(org.id)}
                      onChange={() => handleOrgToggle(org.id)}
                      className="peer appearance-none w-11 h-5 bg-gray-100 rounded-full checked:bg-blue-600 cursor-pointer transition-colors duration-300"
                    />
                    <label
                      htmlFor={`org-switch-${org.id}`}
                      className="absolute top-0 left-0 w-5 h-5 bg-white rounded-full border border-gray-200 shadow-sm transition-transform duration-300 peer-checked:translate-x-6 peer-checked:border-blue-600 cursor-pointer"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Submit Button - Fixed to bottom on mobile */}
          <div className="sticky bottom-0 left-0 right-0 p-4 bg-white border-t shadow-lg md:relative md:border-0 md:shadow-none md:bg-transparent">
            <button
              type="submit"
              disabled={isSubmitting || !formData.text.trim()}
              className={`w-full rounded-xl bg-[#5ABA47] px-6 py-4 text-white font-medium 
                shadow-lg shadow-green-500/30 hover:bg-[#4da53d] 
                focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 
                transition-all duration-200 
                ${(isSubmitting || !formData.text.trim()) && 'opacity-50 cursor-not-allowed'}`}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center space-x-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>Sharing...</span>
                </div>
              ) : (
                'Share Story'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ShareStory; 